// ============================================================================
// WORK ITEM DETAIL CONTEXT
// Shared data provider for WorkItemDetailModal and its tab components.
// Eliminates duplicate API calls by fetching shared data once and letting
// all tabs consume from context.
//
// Shared resources:
//   - operationsUsers: fetched once, used by SubtasksTab, AppointmentsTab, DetailsSidebar
//   - contactLocations: fetched once per contact, used by UnifiedOverviewTab, AppointmentsTab
//   - contactPeople: fetched once per contact, used by UnifiedOverviewTab, AppointmentsTab, SignatureTab
//   - Static data caching: job types, work-item sequences, current user
//
// Date: March 4, 2026
// ============================================================================

'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { authService } from '@/lib/authService';

// ============================================================================
// TYPES
// ============================================================================

export interface OperationsUser {
  id: number;
  first_name?: string;
  last_name?: string;
  name?: string;
  email: string;
  role?: string;
  operations_role?: string;
  [key: string]: any;
}

export interface ContactLocation {
  id: number;
  location_name: string;
  address_line1?: string;
  coordinates?: { lat: number; lng: number } | null;
  [key: string]: any;
}

export interface ContactPerson {
  id: number;
  first_name?: string;
  last_name?: string;
  name?: string;
  email?: string;
  phone?: string;
  [key: string]: any;
}

export interface ContactSummary {
  id: number;
  company_name?: string | null;
  display_name?: string | null;
  contact_person?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  contact_type?: string;
  [key: string]: any;
}

export interface ContactLocationDetail extends ContactLocation {
  contact_id?: number;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  contact_person?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  is_primary?: boolean;
}

interface WorkItemDetailContextValue {
  // ── Operations Users (shared across SubtasksTab, AppointmentsTab, DetailsSidebar) ──
  operationsUsers: OperationsUser[];
  operationsUsersLoading: boolean;
  loadOperationsUsers: () => Promise<OperationsUser[]>;
  hydrateOperationsUsers: (users: OperationsUser[]) => void;

  // ── Contact Locations (shared across UnifiedOverviewTab, AppointmentsTab) ──
  contactLocations: ContactLocation[];
  contactLocationsLoading: boolean;
  loadContactLocations: (contactId: number | string) => Promise<ContactLocation[]>;
  /** Currently loaded contact ID for locations */
  contactLocationsContactId: number | string | null;

  // ── Contact People (shared across UnifiedOverviewTab, AppointmentsTab, FieldServiceSignatureTab) ──
  contactPeople: ContactPerson[];
  contactPeopleLoading: boolean;
  loadContactPeople: (contactId: number | string) => Promise<ContactPerson[]>;
  /** Currently loaded contact ID for people */
  contactPeopleContactId: number | string | null;

  // ── Static Data Cache (persists across modal opens within the same session) ──
  cachedJobTypes: Array<{ key: string; label: string; icon?: string; color?: string }> | null;
  setCachedJobTypes: (types: Array<{ key: string; label: string; icon?: string; color?: string }>) => void;
  cachedCurrentUser: { id: number; name: string; role: string } | null;
  setCachedCurrentUser: (user: { id: number; name: string; role: string }) => void;

  // ── Shared contact lookups (reused by Overview + Appointments tabs) ──
  loadContacts: (options?: { active?: boolean; limit?: number; contactTypes?: string[] }) => Promise<ContactSummary[]>;
  loadContactById: (contactId: number | string) => Promise<ContactSummary | null>;
  loadContactLocationById: (locationId: number | string) => Promise<ContactLocationDetail | null>;

  // ── Utility ──
  getAuthToken: () => string | null;
  /** Reset all per-item data when work item changes */
  resetItemData: () => void;
}

// ============================================================================
// MODULE-LEVEL CACHES (persist across component mounts within the same page session)
// ============================================================================

let _cachedOperationsUsers: OperationsUser[] | null = null;
let _cachedOperationsUsersPromise: Promise<OperationsUser[]> | null = null;
let _cachedJobTypes: Array<{ key: string; label: string; icon?: string; color?: string }> | null = null;
let _cachedCurrentUser: { id: number; name: string; role: string } | null = null;
const _cachedTabConfig: Record<string, string[] | null> = {}; // keyed by item_type
const _cachedContactLists = new Map<string, ContactSummary[]>();
const _cachedContactListPromises = new Map<string, Promise<ContactSummary[]>>();
const _cachedContactsById = new Map<string, ContactSummary | null>();
const _cachedContactByIdPromises = new Map<string, Promise<ContactSummary | null>>();
const _cachedContactLocationsById = new Map<string, ContactLocationDetail | null>();
const _cachedContactLocationByIdPromises = new Map<string, Promise<ContactLocationDetail | null>>();
const _contactPeopleAuthCooldowns = new Map<string, { token: string; retryAfter: number }>();

const CONTACT_PEOPLE_AUTH_COOLDOWN_MS = 30_000;

// ============================================================================
// CONTEXT
// ============================================================================

const WorkItemDetailContext = createContext<WorkItemDetailContextValue | null>(null);

/**
 * Hook to consume WorkItemDetailContext.
 * Returns null if used outside the provider (graceful fallback for standalone tab usage).
 */
export function useWorkItemDetailContext(): WorkItemDetailContextValue | null {
  return useContext(WorkItemDetailContext);
}

/**
 * Hook that requires the context (throws if not inside provider).
 * Use in components that MUST be inside WorkItemDetailModal.
 */
export function useRequiredWorkItemDetailContext(): WorkItemDetailContextValue {
  const ctx = useContext(WorkItemDetailContext);
  if (!ctx) {
    throw new Error('useRequiredWorkItemDetailContext must be used within WorkItemDetailContextProvider');
  }
  return ctx;
}

// ============================================================================
// PROVIDER
// ============================================================================

interface ProviderProps {
  children: ReactNode;
}

export function WorkItemDetailContextProvider({ children }: ProviderProps) {
  // ── Operations Users ──
  const [operationsUsers, setOperationsUsers] = useState<OperationsUser[]>(_cachedOperationsUsers || []);
  const [operationsUsersLoading, setOperationsUsersLoading] = useState(false);

  // ── Contact Locations ──
  const [contactLocations, setContactLocations] = useState<ContactLocation[]>([]);
  const [contactLocationsLoading, setContactLocationsLoading] = useState(false);
  const [contactLocationsContactId, setContactLocationsContactId] = useState<number | string | null>(null);
  const contactLocationsRef = useRef<ContactLocation[]>([]);
  const contactLocationsContactIdRef = useRef<number | string | null>(null);

  // ── Contact People ──
  const [contactPeople, setContactPeople] = useState<ContactPerson[]>([]);
  const [contactPeopleLoading, setContactPeopleLoading] = useState(false);
  const [contactPeopleContactId, setContactPeopleContactId] = useState<number | string | null>(null);
  const contactPeopleRef = useRef<ContactPerson[]>([]);
  const contactPeopleContactIdRef = useRef<number | string | null>(null);

  // ── Static caches (synced from module-level) ──
  const [cachedJobTypes, setCachedJobTypesState] = useState<Array<{ key: string; label: string; icon?: string; color?: string }> | null>(_cachedJobTypes);
  const [cachedCurrentUser, setCachedCurrentUserState] = useState<{ id: number; name: string; role: string } | null>(_cachedCurrentUser);

  // Refs to prevent duplicate in-flight requests
  const contactLocationsRequestRef = useRef<string | null>(null);
  const contactPeopleRequestRef = useRef<string | null>(null);

  useEffect(() => {
    contactLocationsRef.current = contactLocations;
    contactLocationsContactIdRef.current = contactLocationsContactId;
  }, [contactLocations, contactLocationsContactId]);

  useEffect(() => {
    contactPeopleRef.current = contactPeople;
    contactPeopleContactIdRef.current = contactPeopleContactId;
  }, [contactPeople, contactPeopleContactId]);

  const getAuthToken = useCallback((): string | null => {
    return authService.getTokens()?.access_token || null;
  }, []);

  const loadContacts = useCallback(async (options?: {
    active?: boolean;
    limit?: number;
    contactTypes?: string[];
  }): Promise<ContactSummary[]> => {
    const searchParams = new URLSearchParams();

    if (options?.active === true) {
      searchParams.set('active', 'true');
    }

    if (options?.limit) {
      searchParams.set('limit', String(options.limit));
    }

    if (options?.contactTypes && options.contactTypes.length > 0) {
      searchParams.set('contact_types', options.contactTypes.join(','));
    }

    const cacheKey = searchParams.toString() || 'default';
    const cached = _cachedContactLists.get(cacheKey);
    if (cached) {
      return cached;
    }

    const inFlight = _cachedContactListPromises.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    const requestPromise = (async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          return [];
        }

        const response = await fetch(`/api/v1/contacts${cacheKey !== 'default' ? `?${cacheKey}` : ''}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          return [];
        }

        const data = await response.json();
        const contacts = Array.isArray(data.data) ? data.data : [];
        _cachedContactLists.set(cacheKey, contacts);
        contacts.forEach((contact: ContactSummary) => {
          if (contact?.id != null) {
            _cachedContactsById.set(String(contact.id), contact);
          }
        });
        return contacts;
      } catch (error) {
        console.error('Error loading contacts:', error);
        return [];
      } finally {
        _cachedContactListPromises.delete(cacheKey);
      }
    })();

    _cachedContactListPromises.set(cacheKey, requestPromise);
    return requestPromise;
  }, [getAuthToken]);

  const loadContactById = useCallback(async (contactId: number | string): Promise<ContactSummary | null> => {
    const key = String(contactId);
    if (_cachedContactsById.has(key)) {
      return _cachedContactsById.get(key) || null;
    }

    const inFlight = _cachedContactByIdPromises.get(key);
    if (inFlight) {
      return inFlight;
    }

    const requestPromise = (async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          return null;
        }

        const response = await fetch(`/api/v1/contacts/${contactId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          return null;
        }

        const data = await response.json();
        const contact = data.success && data.data ? data.data as ContactSummary : null;
        _cachedContactsById.set(key, contact);
        return contact;
      } catch (error) {
        console.error('Error loading contact by id:', error);
        return null;
      } finally {
        _cachedContactByIdPromises.delete(key);
      }
    })();

    _cachedContactByIdPromises.set(key, requestPromise);
    return requestPromise;
  }, [getAuthToken]);

  const loadContactLocationById = useCallback(async (locationId: number | string): Promise<ContactLocationDetail | null> => {
    const key = String(locationId);
    if (_cachedContactLocationsById.has(key)) {
      return _cachedContactLocationsById.get(key) || null;
    }

    const inFlight = _cachedContactLocationByIdPromises.get(key);
    if (inFlight) {
      return inFlight;
    }

    const requestPromise = (async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          return null;
        }

        const response = await fetch(`/api/v1/contacts/locations/${locationId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          return null;
        }

        const data = await response.json();
        const location = data.success && data.data ? data.data as ContactLocationDetail : null;
        _cachedContactLocationsById.set(key, location);
        return location;
      } catch (error) {
        console.error('Error loading contact location by id:', error);
        return null;
      } finally {
        _cachedContactLocationByIdPromises.delete(key);
      }
    })();

    _cachedContactLocationByIdPromises.set(key, requestPromise);
    return requestPromise;
  }, [getAuthToken]);

  // ── Operations Users: fetch once and cache at module level ──
  const loadOperationsUsers = useCallback(async (): Promise<OperationsUser[]> => {
    // Return cached if available
    if (_cachedOperationsUsers && _cachedOperationsUsers.length > 0) {
      if (operationsUsers.length === 0) setOperationsUsers(_cachedOperationsUsers);
      return _cachedOperationsUsers;
    }

    // Deduplicate in-flight requests
    if (_cachedOperationsUsersPromise) {
      return _cachedOperationsUsersPromise;
    }

    _cachedOperationsUsersPromise = (async () => {
      try {
        setOperationsUsersLoading(true);
        const token = getAuthToken();
        if (!token) return [];

        const response = await fetch('/api/v1/module-access/users?module_key=total-operations&include_operations_role=true', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          const users = data.data || data.users || [];
          _cachedOperationsUsers = users;
          setOperationsUsers(users);
          return users;
        }
        return [];
      } catch (error) {
        console.error('Error loading operations users:', error);
        return [];
      } finally {
        setOperationsUsersLoading(false);
        _cachedOperationsUsersPromise = null;
      }
    })();

    return _cachedOperationsUsersPromise;
  }, [getAuthToken, operationsUsers.length]);

  const hydrateOperationsUsers = useCallback((users: OperationsUser[]) => {
    _cachedOperationsUsers = users;
    _cachedOperationsUsersPromise = null;
    setOperationsUsers(users);
    setOperationsUsersLoading(false);
  }, []);

  // ── Contact Locations: fetch per contact, deduplicate requests ──
  const loadContactLocations = useCallback(async (contactId: number | string): Promise<ContactLocation[]> => {
    const cid = String(contactId);

    // Return cached if same contact
    if (String(contactLocationsContactIdRef.current ?? '') === cid) {
      return contactLocationsRef.current;
    }

    // Deduplicate in-flight requests for same contact
    if (contactLocationsRequestRef.current === cid) {
      return contactLocationsRef.current;
    }

    contactLocationsRequestRef.current = cid;
    try {
      setContactLocationsLoading(true);
      const token = getAuthToken();
      if (!token) return [];

      const response = await fetch(`/api/v1/contacts/${contactId}/locations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const locations = data.data || [];
        setContactLocations(locations);
        setContactLocationsContactId(contactId);
        return locations;
      }
      return [];
    } catch (error) {
      console.error('Error loading contact locations:', error);
      return [];
    } finally {
      setContactLocationsLoading(false);
      contactLocationsRequestRef.current = null;
    }
  }, [getAuthToken]);

  // ── Contact People: fetch per contact, deduplicate requests ──
  const loadContactPeople = useCallback(async (contactId: number | string): Promise<ContactPerson[]> => {
    const cid = String(contactId);

    // Return cached if same contact
    if (String(contactPeopleContactIdRef.current ?? '') === cid) {
      return contactPeopleRef.current;
    }

    // Deduplicate in-flight requests for same contact
    if (contactPeopleRequestRef.current === cid) {
      return contactPeopleRef.current;
    }

    contactPeopleRequestRef.current = cid;
    try {
      setContactPeopleLoading(true);
      const token = getAuthToken();
      if (!token) return [];

      const authCooldown = _contactPeopleAuthCooldowns.get(cid);
      const now = Date.now();
      if (authCooldown && authCooldown.token === token && authCooldown.retryAfter > now) {
        setContactPeople([]);
        setContactPeopleContactId(contactId);
        return [];
      }

      const response = await fetch(`/api/v1/contacts/${contactId}/people?active=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        _contactPeopleAuthCooldowns.delete(cid);
        const data = await response.json();
        const people = data.data || [];
        setContactPeople(people);
        setContactPeopleContactId(contactId);
        return people;
      }

      if (response.status === 401 || response.status === 403) {
        _contactPeopleAuthCooldowns.set(cid, {
          token,
          retryAfter: now + CONTACT_PEOPLE_AUTH_COOLDOWN_MS,
        });
        setContactPeople([]);
        setContactPeopleContactId(contactId);
        return [];
      }

      return [];
    } catch (error) {
      console.error('Error loading contact people:', error);
      return [];
    } finally {
      setContactPeopleLoading(false);
      contactPeopleRequestRef.current = null;
    }
  }, [getAuthToken]);

  // ── Static cache setters (sync to module level) ──
  const setCachedJobTypes = useCallback((types: Array<{ key: string; label: string; icon?: string; color?: string }>) => {
    _cachedJobTypes = types;
    setCachedJobTypesState(types);
  }, []);

  const setCachedCurrentUser = useCallback((user: { id: number; name: string; role: string }) => {
    _cachedCurrentUser = user;
    setCachedCurrentUserState(user);
  }, []);

  // ── Reset per-item data when switching work items ──
  const resetItemData = useCallback(() => {
    setContactLocations([]);
    setContactLocationsContactId(null);
    setContactPeople([]);
    setContactPeopleContactId(null);
    contactLocationsRequestRef.current = null;
    contactPeopleRequestRef.current = null;
  }, []);

  const value: WorkItemDetailContextValue = {
    operationsUsers,
    operationsUsersLoading,
    loadOperationsUsers,
    hydrateOperationsUsers,
    contactLocations,
    contactLocationsLoading,
    loadContactLocations,
    contactLocationsContactId,
    contactPeople,
    contactPeopleLoading,
    loadContactPeople,
    contactPeopleContactId,
    cachedJobTypes,
    setCachedJobTypes,
    cachedCurrentUser,
    setCachedCurrentUser,
    loadContacts,
    loadContactById,
    loadContactLocationById,
    getAuthToken,
    resetItemData,
  };

  return (
    <WorkItemDetailContext.Provider value={value}>
      {children}
    </WorkItemDetailContext.Provider>
  );
}

// ============================================================================
// STATIC DATA CACHE HELPERS
// Used by WorkItemDetailModal to avoid re-fetching on every modal open
// ============================================================================

/**
 * Get cached tab configuration for an item type.
 * Returns undefined if not cached (caller should fetch and cache).
 */
export function getCachedTabConfig(itemType: string): string[] | null | undefined {
  return _cachedTabConfig[itemType];
}

/**
 * Cache tab configuration for an item type.
 */
export function setCachedTabConfig(itemType: string, tabs: string[] | null): void {
  _cachedTabConfig[itemType] = tabs;
}

/**
 * Get cached job types. Returns null if not cached.
 */
export function getCachedJobTypes(): Array<{ key: string; label: string; icon?: string; color?: string }> | null {
  return _cachedJobTypes;
}

/**
 * Get cached current user. Returns null if not cached.
 */
export function getCachedCurrentUser(): { id: number; name: string; role: string } | null {
  return _cachedCurrentUser;
}
